import { type ReactNode } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

import { cn } from '../../utils/cn';
import { Spinner } from '../Spinner';

export interface Column<T> {
  key: keyof T | string;
  header: string;
  sortable?: boolean;
  width?: string;
  render?: (item: T) => ReactNode;
}

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  /** Key extractor - defaults to using 'id' property if available */
  keyExtractor?: (item: T) => string;
  isLoading?: boolean;
  emptyMessage?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (key: string) => void;
  onRowClick?: (item: T) => void;
  /** GAP-004: Selected row ID for three-panel layout highlighting */
  selectedRowId?: string;
  className?: string;
}

export function DataTable<T>({
  data,
  columns,
  keyExtractor,
  isLoading,
  emptyMessage = 'No data available',
  sortBy,
  sortOrder,
  onSort,
  onRowClick,
  selectedRowId,
  className,
}: DataTableProps<T>) {
  const getKey = (item: T, index: number): string => {
    if (keyExtractor) return keyExtractor(item);
    const itemAny = item as Record<string, unknown>;
    if (itemAny.id) return String(itemAny.id);
    return String(index);
  };
  const getCellValue = (item: T, column: Column<T>): ReactNode => {
    if (column.render) {
      return column.render(item);
    }
    const value = item[column.key as keyof T];
    if (value === null || value === undefined) {
      return '-';
    }
    return String(value);
  };

  const handleSort = (column: Column<T>) => {
    if (column.sortable && onSort) {
      onSort(String(column.key));
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className={cn('overflow-x-auto rounded-lg border border-mv-gray-200', className)}>
      <table className="min-w-full divide-y divide-mv-gray-200">
        <thead className="bg-mv-gray-50">
          <tr>
            {columns.map((column) => (
              <th
                key={String(column.key)}
                scope="col"
                style={{ width: column.width }}
                className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-mv-gray-500"
              >
                {column.sortable ? (
                  <button
                    type="button"
                    onClick={() => handleSort(column)}
                    className="flex w-full items-center gap-1 text-left hover:bg-mv-gray-100 focus:outline-none focus:ring-2 focus:ring-mv-blue-500 focus:ring-offset-2"
                    aria-label={`Sort by ${column.header}${sortBy === column.key ? ` (currently ${sortOrder === 'asc' ? 'ascending' : 'descending'})` : ''}`}
                  >
                    {column.header}
                    {sortBy === column.key && (
                      sortOrder === 'asc' ? (
                        <ChevronUpIcon className="h-4 w-4" aria-hidden="true" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
                      )
                    )}
                  </button>
                ) : (
                  <div className="flex items-center gap-1">
                    {column.header}
                  </div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-mv-gray-200 bg-white">
          {data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-12 text-center text-sm text-mv-gray-500"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((item, index) => {
              const rowKey = getKey(item, index);
              const isSelected = selectedRowId === rowKey;
              return (
              <tr
                key={rowKey}
                className={cn(
                  'transition-colors',
                  onRowClick && 'cursor-pointer hover:bg-mv-gray-50',
                  isSelected && 'bg-mv-blue-50 hover:bg-mv-blue-100'
                )}
                onClick={() => onRowClick?.(item)}
                aria-selected={isSelected}
              >
                {columns.map((column) => (
                  <td
                    key={String(column.key)}
                    className="whitespace-nowrap px-4 py-3 text-sm text-mv-gray-700"
                  >
                    {getCellValue(item, column)}
                  </td>
                ))}
              </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
